// CHG-123 REQ-V123-050: 메시지 버블 하단 linkedResources 배지 + 펼침 패널
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, CheckSquare } from 'lucide-react';
import type { LinkedResources } from '../../services/chatService';

interface Props {
  linkedResources?: LinkedResources;
  isMine: boolean;
}

export default function MessageLinkedResourcesBadge({ linkedResources, isMine }: Props) {
  const navigate = useNavigate();
  const [openPanel, setOpenPanel] = useState<'issues' | 'todos' | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 펼침 패널 닫기
  useEffect(() => {
    if (!openPanel) return;
    const handle = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpenPanel(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [openPanel]);

  const issues = linkedResources?.issues ?? [];
  const todos = linkedResources?.todos ?? [];

  // 둘 다 비어있으면 렌더 안 함
  if (issues.length === 0 && todos.length === 0) return null;

  // 솔리드 배경·명시적 색상으로 캐시·확장 프로그램 영향 최소화
  const badgeBase = `relative z-10 inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium border transition-colors ${
    isMine
      ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
  }`;

  const firstIssue = issues[0];
  const restIssueCount = issues.length - 1;

  return (
    <div ref={wrapperRef} className="relative mt-1 flex items-center gap-1 z-10">
      {/* 작업 배지 */}
      {issues.length > 0 && (
        <button
          type="button"
          onClick={() => setOpenPanel(openPanel === 'issues' ? null : 'issues')}
          className={badgeBase}
          aria-label={`연결된 작업 ${issues.length}건 보기`}
          aria-expanded={openPanel === 'issues'}
        >
          <Briefcase width={12} height={12} strokeWidth={2.25} />
          <span>{firstIssue.issueKey}</span>
          {restIssueCount > 0 && <span className="opacity-70">+{restIssueCount}건</span>}
        </button>
      )}

      {/* 할 일 배지 */}
      {todos.length > 0 && (
        <button
          type="button"
          onClick={() => setOpenPanel(openPanel === 'todos' ? null : 'todos')}
          className={badgeBase}
          aria-label={`연결된 할 일 ${todos.length}건 보기`}
          aria-expanded={openPanel === 'todos'}
        >
          <CheckSquare width={12} height={12} strokeWidth={2.25} />
          <span>할 일 {todos.length}건</span>
        </button>
      )}

      {/* 펼침 패널 — 이슈 */}
      {openPanel === 'issues' && issues.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-30 w-80 max-h-72 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 p-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-1 mb-1">연결된 작업 ({issues.length})</p>
          <ul className="space-y-1">
            {issues.map((it) => (
              <li key={it.issueId}>
                <button
                  type="button"
                  onClick={() => {
                    setOpenPanel(null);
                    navigate(`/projects/${it.projectKey}/issues?panel=${it.issueId}`);
                  }}
                  className="w-full flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-primary-50 text-left"
                >
                  <span className="text-sm font-semibold text-primary-600 shrink-0">{it.issueKey}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">{it.title}</p>
                  </div>
                  <span
                    className="text-[11px] px-1.5 py-0.5 rounded shrink-0"
                    style={{ backgroundColor: `${it.statusColor}22`, color: it.statusColor }}
                  >
                    {it.statusName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 펼침 패널 — 할 일 */}
      {openPanel === 'todos' && todos.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-30 w-72 max-h-72 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 p-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-1 mb-1">내 할 일 ({todos.length})</p>
          <ul className="space-y-1">
            {todos.map((td) => (
              <li key={td.todoId}>
                <button
                  type="button"
                  onClick={() => {
                    setOpenPanel(null);
                    navigate(`/?focusTodo=${td.todoId}`);
                  }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-primary-50 text-left"
                >
                  <CheckSquare className={`size-3.5 shrink-0 ${td.completed ? 'text-primary-500' : 'text-gray-300'}`} />
                  <p className={`text-sm truncate flex-1 ${td.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {td.title}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
